import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Heart, Sparkles, Instagram, Youtube, Facebook } from 'lucide-react';
import Logo from './Logo';

const Footer = ({ setActiveSection }) => {
  const handleNavClick = (section) => {
    setActiveSection(section);
  };

  const socialLinks = [
    {
      name: "Gmail",
      url: "mailto:help.agoinnovations@gmail.com",
      icon: Mail,
      color: "from-red-500 to-red-600",
      hoverColor: "hover:from-red-400 hover:to-red-500"
    },
    {
      name: "Instagram",
      url: "https://instagram.com/ago.innovations",
      icon: Instagram,
      color: "from-pink-500 to-purple-600",
      hoverColor: "hover:from-pink-400 hover:to-purple-500"
    },
    {
      name: "YouTube",
      url: "https://youtube.com/@ago.innovations",
      icon: Youtube,
      color: "from-red-600 to-red-700",
      hoverColor: "hover:from-red-500 hover:to-red-600"
    },
    {
      name: "Facebook",
      url: "https://facebook.com/AgoinnovationsOfficial",
      icon: Facebook,
      color: "from-blue-600 to-blue-700",
      hoverColor: "hover:from-blue-500 hover:to-blue-600"
    }
  ];

  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: "help.agoinnovations@gmail.com",
      color: "from-blue-500 to-indigo-500"
    },
    {
      icon: Phone,
      label: "Phone",
      value: "+91 9322578756",
      color: "from-emerald-500 to-green-500"
    },
    {
      icon: Phone,
      label: "Phone",
      value: "+91 9372663841",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Phone,
      label: "Phone",
      value: "+91 7738340929",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: MapPin,
      label: "Address",
      value: "Mumbai, India",
      color: "from-cyan-500 to-blue-500"
    }
  ];

  const quickLinks = [
    { name: 'Home', path: '/', section: 'home' },
    { name: 'Learn & Earn', path: '/learn', section: 'learn' },
    { name: 'Assignment Writing', path: '/assignment-writing-service', section: 'assignment-writing-service' },
    { name: 'Student Jobs', path: '/student-jobs-earn-money', section: 'student-jobs-earn-money' },
    { name: 'Blog', path: '/blog', section: 'blog' },
    { name: 'About Us', path: '/about', section: 'about' },
    { name: 'Contact', path: '/contact', section: 'contact' },
    { name: 'Pricing & Policies', path: '/pricing-policy', section: 'pricing-policy' },
  ];

  // Location links for local SEO — anchor text matters
  const locationLinks = [
    { name: 'Assignment Help Mumbai', path: '/assignment-help-mumbai' },
    { name: 'Assignment Help Pune', path: '/assignment-help-pune' },
    { name: 'Assignment Help Navi Mumbai', path: '/assignment-help-navi-mumbai' },
    { name: 'Assignment Help Thane', path: '/assignment-help-thane' },
    { name: 'Insurance Services Mumbai', path: '/insurance-services-mumbai' },
    { name: 'Insurance Services Pune', path: '/insurance-services-pune' },
  ];

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-64 h-64 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-20 right-20 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-10 left-1/2 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>
      
      {/* Decorative Top Border */}
      <div className="h-1 bg-gradient-to-r from-emerald-400 via-blue-400 to-purple-400"></div>
      
      <div className="relative py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Trust Line Above Footer */}
          <div className="text-center mb-12 pb-8 border-b border-white/10">
            <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500/20 to-blue-500/20 backdrop-blur-sm rounded-full border border-white/10 mb-4">
              <Sparkles className="w-5 h-5 text-emerald-400 mr-2" />
              <p className="text-emerald-100 font-medium">
                A.G.O is committed to empowering individuals through informed decisions.
              </p>
              <Sparkles className="w-5 h-5 text-blue-400 ml-2" />
            </div>
          </div>
          
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8 mb-12">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="flex items-center mb-6">
                <Logo className="w-12 h-12" />
                <div className="ml-4">
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent">
                    A.G.O Innovations
                  </h3>
                  <p className="text-emerald-200 text-sm">Team Incredible</p>
                </div>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed text-sm">
                India's student services &amp; finance marketplace — assignment writing, workshops, student earning, insurance advisory and investment planning.
              </p>
            </div>
            
            {/* Quick Links */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent">
                Quick Links
              </h3>
              <ul className="space-y-2">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <Link 
                      to={link.path}
                      onClick={() => handleNavClick(link.section)}
                      className="group flex items-center text-gray-300 hover:text-emerald-400 transition-all duration-300 text-sm"
                    >
                      <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <span className="group-hover:translate-x-1 transition-transform duration-300">
                        {link.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Location SEO Links */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent">
                Service Locations
              </h3>
              <ul className="space-y-2">
                {locationLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.path}
                      className="group flex items-center text-gray-300 hover:text-emerald-400 transition-all duration-300 text-sm"
                    >
                      <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <span className="group-hover:translate-x-1 transition-transform duration-300">
                        {link.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Contact Info */}
            <div>
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent">
                Contact Us
              </h3>
              <ul className="space-y-4">
                {contactInfo.map((contact, index) => {
                  const IconComponent = contact.icon;
                  return (
                    <li key={index} className="group">
                      <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 transition-all duration-300">
                        <div className={`w-8 h-8 bg-gradient-to-r ${contact.color} rounded-lg flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                          <IconComponent className="text-white" size={16} />
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide">{contact.label}</p>
                          <p className="text-gray-200 group-hover:text-white transition-colors duration-300 text-sm">
                            {contact.value}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
          
          {/* Bottom Section */}
          <div className="pt-8 border-t border-white/10">
            {/* Social Media Links */}
            <div className="text-center mb-8">
              <h3 className="text-xl font-bold mb-6 bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent">
                Connect With Us
              </h3>
              <div className="flex justify-center space-x-6">
                {socialLinks.map((social, index) => {
                  const IconComponent = social.icon;
                  return (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`group relative p-4 bg-gradient-to-r ${social.color} ${social.hoverColor} rounded-full shadow-lg transform hover:scale-110 transition-all duration-300`}
                      title={social.name}
                    >
                      <IconComponent className="w-6 h-6 text-white" />
                      <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                        {social.name}
                      </div>
                    </a>
                  );
                })}
              </div>
              <div className="mt-6 text-sm text-gray-400 space-y-1">
                <p><strong>Gmail:</strong> help.agoinnovations@gmail.com</p>
                <p><strong>Instagram:</strong> ago.innovations</p>
                <p><strong>YouTube:</strong> @ago.innovations</p>
                <p><strong>Facebook:</strong> A.G.O Innovations</p>
              </div>
            </div>
            
            <div className="text-center">
              <div className="mb-4">
                <span className="text-lg font-bold text-white">&copy; 2025 A.G.O Innovations. All rights reserved.</span>
              </div>
              <div className="flex items-center justify-center space-x-1">
                <span className="text-gray-400">Made with</span>
                <Heart className="w-4 h-4 text-red-400 animate-pulse" />
                <span className="text-gray-400">in India</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Gradient Border */}
      <div className="h-1 bg-gradient-to-r from-purple-400 via-blue-400 to-emerald-400"></div>
    </footer>
  );
};

export default Footer;